import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import spots from './spots.json';
import { MemoryCard } from './MemoryCard';
import { GalleryModal } from './GalleryModal';
import { ResilientImg } from './ResilientImg';
import { CmBackButton } from './CmBackButton';

// 只有出現次數 >= N 的 tag 才會出現在地圖上的篩選列
// （避免 filter 出現太多只用過一次的冷門 tag）
// 目前資料量較小，改成 1 避免整組分類都被隱藏
const MIN_TAG_COUNT_FOR_FILTER = 2;

const TAG_CATEGORIES = [
  { id: 'food', label: '食物', tags: ['拉麵','牛肉麵','炒飯','麥當勞','永豆','火鍋','壽司','牛舌','麥味登','鐵板燒','餃子','熱炒','林美如熱炒','韓式','肉肉大米','subway','福勝亭','湯咖喱','早餐','晚餐','宵夜','早午餐','美食','甜點','草莓大福','草莓蛋糕','調酒','咖啡廳','星巴克','餐酒館','焦糖風','永心鳳茶','酒吧','九半商事','bar aku','青春雞','路易吉洗衣店'] },
  { id: 'activity', label: '活動', tags: ['散步','聊天','電影','DJ','日文','工作','閱讀','購物','送行','接下班','開車','Goshare','Uber'] },
  { id: 'location', label: '地點', tags: ['板橋','頭城','中山','信義','市政府','南港','新埔','忠孝復興','華山光點','善導寺','新月橋','復興','府中','北車','公司','DT','誠品','松仁威秀'] },
  { id: 'special', label: '特別', tags: ['💗','HARE','朋朋','花','卡片','帽帽','星星','香水','山茶花洗髮精','Yumi 展','跨年','生理假','感冒','照顧','查理','晚班','電話','夜晚','深夜','布達佩斯大飯店','雙囍','美髮','港口','博物館','沙灘'] },
];

const SPOT_BY_ID = Array.isArray(spots)
  ? spots.reduce((map, spot) => {
      if (spot && spot.id) map[spot.id] = spot;
      return map;
    }, {})
  : {};

function createMapPinIcon(label, isCluster = false) {
  const baseOptions = {
    className: `charley-memory-map-pin${isCluster ? ' charley-memory-map-pin--cluster' : ''}`,
    iconSize: [30, 34],
    iconAnchor: [15, 30],
  };

  if (!isCluster) {
    return L.divIcon({
      ...baseOptions,
      html: `<span class="map-pin-dot"><span class="map-pin-num">${label}</span></span>`,
    });
  }

  return L.divIcon({
    ...baseOptions,
    html: `
      <div class="map-pin-cluster">
        <span class="map-pin-dot map-pin-dot--back"></span>
        <span class="map-pin-dot map-pin-dot--mid"></span>
        <span class="map-pin-dot map-pin-dot--front">
          <span class="map-pin-num map-pin-num--cluster">${label}</span>
        </span>
      </div>
    `,
  });
}

function getSegmentColor(index, totalSegments) {
  if (!totalSegments || totalSegments <= 1) {
    return 'rgb(8, 77, 169)'; // start color
  }
  const t = index / (totalSegments - 1);
  const start = { r: 136, g: 187, b: 234 };   // 較淡的藍色
  const end = { r: 8, g: 77, b: 169 }; // 較深的藍色
  const r = Math.round(start.r + (end.r - start.r) * t);
  const g = Math.round(start.g + (end.g - start.g) * t);
  const b = Math.round(start.b + (end.b - start.b) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

function FitAllMarkers({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
    } else {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [map, points]);
  return null;
}

const MapViewContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #0a0c10;
  z-index: 1500;
`;

const MapHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(7, 10, 18, 0.85);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: #e9ecf1;
`;

const MapTitle = styled.span`
  font-weight: 600;
  font-size: 1rem;
`;

const MapExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: #4285f4;
  text-decoration: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.4);
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    background: #ffffff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  }
`;

const GoogleFavicon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 11px;
  color: #4285f4;
`;

const MapWrap = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  .charley-memory-map-pin {
    background: none;
    border: none;
  }
  .map-pin-dot {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin: 0 auto;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    background: #5b8fd6;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
    border: 2px solid rgba(255, 255, 255, 0.9);
  }
  .map-pin-cluster {
    position: relative;
    width: 32px;
    height: 34px;
  }
  .charley-memory-map-pin--cluster .map-pin-dot {
    position: absolute;
    width: 24px;
    height: 24px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
    border: 2px solid rgba(255, 255, 255, 0.7);
  }
  .map-pin-dot--back {
    left: 2px;
    top: 6px;
    background: rgba(91, 143, 214, 0.45);
  }
  .map-pin-dot--mid {
    right: 2px;
    top: 4px;
    background: rgba(91, 143, 214, 0.7);
  }
  .map-pin-dot--front {
    left: 4px;
    bottom: 0;
    background: #5b8fd6;
    border-width: 3px;
  }
  .map-pin-num {
    transform: rotate(45deg);
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    line-height: 1;
  }
  .map-pin-num--cluster {
    font-size: 12px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  }
  .leaflet-container {
    z-index: 0;
  }
  .leaflet-control-container .leaflet-top.leaflet-left {
    top: 64px;
  }
  .leaflet-popup-content-wrapper {
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
  .leaflet-popup-content {
    margin: 10px 14px;
    font-size: 0.9rem;
  }
`;

const SpotSheet = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  max-height: 70vh;
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.97) 0%, rgba(12, 14, 20, 0.99) 100%);
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  overflow: hidden;
  animation: sheetUp 0.25s ease-out;
  @keyframes sheetUp {
    from { transform: translateY(100%); opacity: 0.8; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const SheetHandle = styled.div`
  flex-shrink: 0;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  &::after {
    content: '';
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.3);
  }
`;

const SheetTitle = styled.div`
  flex-shrink: 0;
  /* 預留右上角 CloseSheetBtn（36px + 右側 12px + 緩衝）避免重疊 */
  padding: 0 64px 12px 20px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #e9ecf1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const EntryList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 24px;
  -webkit-overflow-scrolling: touch;
`;

const EntryChip = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  margin-bottom: 8px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  color: #e9ecf1;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
  &:last-child {
    margin-bottom: 0;
  }
`;

const EntryChipText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PhotoStack = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const StackedThumb = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  transform: rotate(${({ $rotation = 0 }) => $rotation}deg);
  transform-origin: 50% 50%;
  &:not(:first-child) {
    margin-left: -14px;
  }
`;

const EntryChipDate = styled.span`
  opacity: 0.75;
  margin-right: 8px;
`;

const CardInSheet = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

const CloseSheetBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: #e9ecf1;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  z-index: 2;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const SheetMiniCardButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.06);
  color: #e9ecf1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45);
  }
`;

const FilterBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 500;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 0 6px;
`;

const FilterTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px 4px;
  flex-wrap: wrap;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 6px;
  padding: 0 12px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  mask-image: linear-gradient(to right, transparent 0px, black 12px, black calc(100% - 12px), transparent 100%);
  -webkit-mask-image: linear-gradient(to right, transparent 0px, black 12px, black calc(100% - 12px), transparent 100%);
`;

const FilterChip = styled.button`
  flex-shrink: 0;
  padding: 6px 14px;
  border: 1.5px solid ${(p) => (p.$active ? 'rgba(91, 143, 214, 0.8)' : 'rgba(255, 255, 255, 0.2)')};
  border-radius: 20px;
  background: ${(p) => (p.$active ? 'rgba(91, 143, 214, 0.25)' : 'rgba(10, 12, 16, 0.7)')};
  color: ${(p) => (p.$active ? '#a8ccf0' : '#c8ccd4')};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.15s ease;
  white-space: nowrap;
  &:hover {
    background: ${(p) => (p.$active ? 'rgba(91, 143, 214, 0.35)' : 'rgba(255, 255, 255, 0.12)')};
  }
`;

const DateFilterControl = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DateNavBtn = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255, 211, 110, 0.45);
  background: rgba(10, 12, 16, 0.9);
  color: rgba(255, 211, 110, 0.95);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: transform 0.12s ease, background 0.12s ease, border-color 0.12s ease;
  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 211, 110, 0.12);
    border-color: rgba(255, 211, 110, 0.7);
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
    transform: none;
  }
`;

const DatePickerWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  pointer-events: auto;
`;

const DatePickerBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(10, 12, 16, 0.9);
  color: #e9ecf1;
  font-size: 0.8rem;
  cursor: pointer;
  outline: none;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35);
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.12s ease;
  &:hover {
    transform: translateY(-1px);
    background: rgba(15, 18, 24, 0.95);
    border-color: rgba(91, 143, 214, 0.6);
  }
`;

const DatePickerPopover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 600;
  width: min(320px, calc(100vw - 24px));
  border-radius: 12px;
  padding: 10px 10px 12px;
  background: rgba(10, 13, 22, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(10px);

  /* v9 主要容器 class 是 rdp-root（但也兼容 rdp） */
  .rdp-root,
  .rdp {
    --rdp-cell-size: 34px;
    /* 選取/強調顏色：亮一點、避免太藍看不見 */
    --rdp-accent-color: #ffd36e;
    --rdp-accent-color-dark: #ffd36e;
    --rdp-background-color: rgba(255, 211, 110, 0.18);
    color: #e9ecf1;
    margin: 0;
  }
  .rdp-months {
    justify-content: flex-start;
  }
  .rdp-month {
    margin: 0;
  }
  .rdp-month_caption {
    justify-content: flex-start;
    font-weight: 700;
    margin-bottom: 8px;
  }
  .rdp-nav {
    gap: 6px;
  }
  .rdp-nav_button {
    color: rgba(255, 211, 110, 0.95);
    opacity: 0.95;
  }
  .rdp-nav_button:hover {
    background: rgba(255, 211, 110, 0.12);
  }
  .rdp-caption_label {
    text-align: left;
  }
  .rdp-button {
    border-radius: 10px;
  }
  .rdp-day {
    border-radius: 10px;
  }
  .rdp-day_hasData:not(.rdp-day_selected) {
    outline: 1px solid rgba(255, 211, 110, 0.35);
    background: rgba(255, 211, 110, 0.08);
  }
  .rdp-day_selected {
    background: rgba(255, 211, 110, 0.34);
    outline: 2px solid rgba(255, 211, 110, 0.95);
    color: #ffffff;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35), 0 10px 26px rgba(0, 0, 0, 0.35);
    font-weight: 750;
  }
  /* 「今天」預設會用 accent color 畫文字/框線，這裡改成偏暖更顯眼 */
  .rdp-day_today:not(.rdp-day_selected) {
    color: rgba(255, 211, 110, 0.98);
    outline: 1px dashed rgba(255, 211, 110, 0.65);
    outline-offset: -2px;
  }
  .rdp-day_disabled {
    opacity: 0.25;
  }
`;

const ClearDateButton = styled.button`
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 120, 120, 0.5);
  background: rgba(80, 20, 20, 0.7);
  color: #f0a0a0;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: rgba(120, 40, 40, 0.9);
    border-color: rgba(255, 150, 150, 0.9);
  }
`;

export function CharleyMemoryMapView({ content, getEntryPhotos, getPhotoRotation, onBack, onTagClick }) {
  const [selectedSpotData, setSelectedSpotData] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedPathItem, setSelectedPathItem] = useState(null);
  const [galleryEntry, setGalleryEntry] = useState(null);
  const [galleryInitialSrc, setGalleryInitialSrc] = useState(null);
  const [galleryPhotosOverride, setGalleryPhotosOverride] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [dateFilter, setDateFilter] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const galleryScrollRef = useRef(null);
  const galleryIndexRef = useRef(0);
  const datePickerWrapRef = useRef(null);

  const contentForTagOptions = useMemo(() => {
    if (!dateFilter) return content;
    if (!Array.isArray(content)) return content;
    return content.filter((entry) => {
      const entryDate = typeof entry?.timestamp === 'string' ? entry.timestamp.slice(0, 10) : '';
      return entryDate === dateFilter;
    });
  }, [content, dateFilter]);

  const availableDateKeys = useMemo(() => {
    const set = new Set();
    if (!Array.isArray(content)) return set;
    content.forEach((entry) => {
      const dateKey = typeof entry?.timestamp === 'string' ? entry.timestamp.slice(0, 10) : '';
      if (dateKey) set.add(dateKey);
    });
    return set;
  }, [content]);

  const sortedAvailableDateKeys = useMemo(() => {
    const keys = Array.from(availableDateKeys);
    keys.sort((a, b) => {
      const ta = new Date(`${a}T00:00:00`).getTime();
      const tb = new Date(`${b}T00:00:00`).getTime();
      return ta - tb;
    });
    return keys;
  }, [availableDateKeys]);

  const availableDateObjects = useMemo(() => {
    const keys = Array.from(availableDateKeys);
    return keys
      .map((k) => new Date(`${k}T00:00:00`))
      .filter((d) => !Number.isNaN(d.getTime()));
  }, [availableDateKeys]);

  const { minAvailableDate, maxAvailableDate } = useMemo(() => {
    if (!availableDateObjects.length) return { minAvailableDate: undefined, maxAvailableDate: undefined };
    const sorted = [...availableDateObjects].sort((a, b) => a.getTime() - b.getTime());
    return { minAvailableDate: sorted[0], maxAvailableDate: sorted[sorted.length - 1] };
  }, [availableDateObjects]);

  const selectedDateObject = useMemo(() => {
    if (!dateFilter) return undefined;
    const d = new Date(`${dateFilter}T00:00:00`);
    if (Number.isNaN(d.getTime())) return undefined;
    return d;
  }, [dateFilter]);

  const canGoPrevDate = useMemo(() => {
    if (!sortedAvailableDateKeys.length) return false;
    if (!dateFilter) return sortedAvailableDateKeys.length > 0;
    return sortedAvailableDateKeys.indexOf(dateFilter) > 0;
  }, [dateFilter, sortedAvailableDateKeys]);

  const canGoNextDate = useMemo(() => {
    if (!sortedAvailableDateKeys.length) return false;
    if (!dateFilter) return sortedAvailableDateKeys.length > 0;
    const idx = sortedAvailableDateKeys.indexOf(dateFilter);
    return idx !== -1 && idx < sortedAvailableDateKeys.length - 1;
  }, [dateFilter, sortedAvailableDateKeys]);

  const goPrevDate = useCallback(() => {
    if (!sortedAvailableDateKeys.length) return;
    if (!dateFilter) {
      setDateFilter(sortedAvailableDateKeys[sortedAvailableDateKeys.length - 1]);
      setDatePickerOpen(false);
      return;
    }
    const idx = sortedAvailableDateKeys.indexOf(dateFilter);
    if (idx > 0) {
      setDateFilter(sortedAvailableDateKeys[idx - 1]);
      setDatePickerOpen(false);
    }
  }, [dateFilter, sortedAvailableDateKeys]);

  const goNextDate = useCallback(() => {
    if (!sortedAvailableDateKeys.length) return;
    if (!dateFilter) {
      setDateFilter(sortedAvailableDateKeys[0]);
      setDatePickerOpen(false);
      return;
    }
    const idx = sortedAvailableDateKeys.indexOf(dateFilter);
    if (idx !== -1 && idx < sortedAvailableDateKeys.length - 1) {
      setDateFilter(sortedAvailableDateKeys[idx + 1]);
      setDatePickerOpen(false);
    }
  }, [dateFilter, sortedAvailableDateKeys]);

  useEffect(() => {
    if (!datePickerOpen) return;
    const onDown = (e) => {
      const wrap = datePickerWrapRef.current;
      if (!wrap) return;
      if (wrap.contains(e.target)) return;
      setDatePickerOpen(false);
    };
    window.addEventListener('mousedown', onDown, { capture: true });
    return () => window.removeEventListener('mousedown', onDown, { capture: true });
  }, [datePickerOpen]);

  const availableTags = useMemo(() => {
    const tagCount = new Map();
    if (!Array.isArray(contentForTagOptions)) return [];
    contentForTagOptions.forEach((entry) => {
      if (!Array.isArray(entry.path) || !entry.path.length) return;
      entry.path.forEach((p) => {
        const spot = SPOT_BY_ID[p?.spotId];
        if (!spot || typeof spot.lat !== 'number') return;
        const pathTags = Array.isArray(p.tags) ? p.tags : [];
        const spotTags = Array.isArray(spot.tags) ? spot.tags : [];
        // 同一個 path + spot 上相同的 tag 只算一次，避免重複加總
        const mergedTagSet = new Set([...pathTags, ...spotTags].filter(Boolean));
        mergedTagSet.forEach((t) => {
          tagCount.set(t, (tagCount.get(t) || 0) + 1);
        });
      });
    });
    return Array.from(tagCount.entries())
      .filter(([, count]) => count >= MIN_TAG_COUNT_FOR_FILTER)
      .sort((a, b) => b[1] - a[1])
      .map(([tag]) => tag);
  }, [contentForTagOptions]);

  const availableTagSet = useMemo(() => new Set(availableTags), [availableTags]);

  const availableCategories = useMemo(() => {
    return TAG_CATEGORIES
      .map((cat) => {
        const visibleTags = cat.tags.filter((t) => availableTagSet.has(t));
        return visibleTags.length > 0 ? { ...cat, tags: visibleTags } : null;
      })
      .filter(Boolean);
  }, [availableTagSet]);

  useEffect(() => {
    // 日期篩選後，若原本選到的 tag 已不存在就自動清掉（避免卡住）
    setActiveFilters((prev) => {
      if (!(prev instanceof Set) || prev.size === 0) return prev;
      const next = new Set(Array.from(prev).filter((t) => availableTagSet.has(t)));
      return next.size === prev.size ? prev : next;
    });

    if (expandedCategory) {
      const stillExists = availableCategories.some((c) => c.id === expandedCategory);
      if (!stillExists) setExpandedCategory(null);
    }
  }, [dateFilter, availableTagSet, availableCategories, expandedCategory]);

  const getPathItemPhotos = useCallback((pathItem) => {
    if (!pathItem || typeof pathItem !== 'object') return [];
    const images = Array.isArray(pathItem.images) ? pathItem.images : [];
    return images
      .map((img) => (img && typeof img === 'object' ? img.url : img))
      .filter(Boolean);
  }, []);

  // 當天所有 entry 的所有 path image（不管 tag/filter），回傳 meta：{ src, entryId }
  const getDatePathPhotoMeta = useCallback((dateKey) => {
    if (!dateKey || !Array.isArray(content)) return [];
    const list = [];
    content.forEach((entry) => {
      const entryDate = typeof entry?.timestamp === 'string' ? entry.timestamp.slice(0, 10) : '';
      if (entryDate !== dateKey) return;
      if (!Array.isArray(entry.path)) return;
      entry.path.forEach((pathItem) => {
        const imgs = Array.isArray(pathItem?.images) ? pathItem.images : [];
        imgs.forEach((img) => {
          const src = img && typeof img === 'object' ? img.url : img;
          if (!src) return;
          list.push({ src, entryId: entry?.id });
        });
      });
    });
    return list;
  }, [content]);

  const getDatePathPhotoSrcs = useCallback(
    (dateKey) => getDatePathPhotoMeta(dateKey).map((p) => p.src),
    [getDatePathPhotoMeta]
  );

  // 提供給 MapView 內嵌的 MemoryCard：當從 path entry 點進卡片時，
  // 卡片的照片縮圖要顯示「當天所有 path image」，否則回到預設的 entry 照片邏輯。
  const getEntryPhotosForSelectedPath = useCallback(
    (entry) => {
      if (!entry) return [];
      if (selectedPathItem) {
        const dateKey =
          typeof entry.timestamp === 'string' ? entry.timestamp.slice(0, 10) : '';
        return getDatePathPhotoSrcs(dateKey);
      }
      return getEntryPhotos(entry);
    },
    [getDatePathPhotoSrcs, getEntryPhotos, selectedPathItem]
  );

  // 先依「所有 path item 的出現順序」編好全域編號（1-based）
  const pathPoints = useMemo(() => {
    const list = [];
    if (!Array.isArray(content)) return list;
    content.forEach((entry) => {
      if (!Array.isArray(entry.path)) return;
      entry.path.forEach((pathItem) => {
        const spotId = pathItem?.spotId;
        if (!spotId) return;
        const spot = SPOT_BY_ID[spotId];
        if (!spot || typeof spot.lat !== 'number' || typeof spot.lng !== 'number') return;

        // Map 上的篩選同時看 path.tags + spot.tags，再加上內文比對（path.note / entry.title/summary/details）
        if (activeFilters.size > 0) {
          const pathTags = Array.isArray(pathItem.tags) ? pathItem.tags : [];
          const spotTags = Array.isArray(spot.tags) ? spot.tags : [];
          const hasTagMatch = [...pathTags, ...spotTags].some((t) => activeFilters.has(t));
          let hasTextMatch = false;
          if (!hasTagMatch) {
            const textFields = [pathItem?.note, entry?.title, entry?.summary, entry?.details]
              .filter((s) => typeof s === 'string' && s.length > 0)
              .map((s) => s.toLowerCase());
            if (textFields.length > 0) {
              for (const tag of activeFilters) {
                const needle = String(tag).toLowerCase();
                if (textFields.some((text) => text.includes(needle))) {
                  hasTextMatch = true;
                  break;
                }
              }
            }
          }
          if (!hasTagMatch && !hasTextMatch) return;
        }

        if (dateFilter) {
          const entryDate = typeof entry.timestamp === 'string' ? entry.timestamp.slice(0, 10) : '';
          if (entryDate !== dateFilter) return;
        }
        const t = entry?.timestamp ? new Date(entry.timestamp).getTime() : Infinity;
        list.push({
          entry,
          pathItem,
          spot,
          timestampValue: t,
        });
      });
    });
    // 依 timestamp 排序，但保持同一天內 path 的原始順序
    list.sort((a, b) => a.timestampValue - b.timestampValue);
    return list.map((item, index) => ({
      ...item,
      pinNumber: index + 1,
    }));
  }, [content, activeFilters, dateFilter]);

  const pathLineSegments = useMemo(() => {
    if (!dateFilter) return [];
    if (!Array.isArray(pathPoints) || pathPoints.length < 2) return [];
    const segments = [];
    for (let i = 0; i < pathPoints.length - 1; i += 1) {
      const current = pathPoints[i];
      const next = pathPoints[i + 1];
      if (
        !current ||
        !next ||
        !current.spot ||
        !next.spot ||
        typeof current.spot.lat !== 'number' ||
        typeof current.spot.lng !== 'number' ||
        typeof next.spot.lat !== 'number' ||
        typeof next.spot.lng !== 'number'
      ) {
        continue;
      }
      segments.push({
        from: { lat: current.spot.lat, lng: current.spot.lng },
        to: { lat: next.spot.lat, lng: next.spot.lng },
      });
    }
    return segments;
  }, [dateFilter, pathPoints]);

  // 地圖上的點位：一個 spot 一個 point，用「該 spot 底下所有 path 中最小的 pinNumber」當作地圖上的編號
  const mapPoints = useMemo(() => {
    const bySpot = new Map();
    pathPoints.forEach((p) => {
      const spotId = p.spot.id;
      if (!bySpot.has(spotId)) {
        bySpot.set(spotId, {
          spot: p.spot,
          entries: [],
          firstPinNumber: p.pinNumber,
          firstTimestampValue: p.timestampValue,
        });
      }
      const group = bySpot.get(spotId);
      group.entries.push(p);
      if (p.pinNumber < group.firstPinNumber) {
        group.firstPinNumber = p.pinNumber;
      }
      if (p.timestampValue < group.firstTimestampValue) {
        group.firstTimestampValue = p.timestampValue;
      }
    });
    return Array.from(bySpot.values())
      .sort((a, b) => a.firstTimestampValue - b.firstTimestampValue)
      .map((group) => ({
        id: group.spot.id,
        lat: group.spot.lat,
        lng: group.spot.lng,
        name: group.spot.name,
        spot: group.spot,
        entries: group.entries,
        pinNumber: group.firstPinNumber,
        firstTimestampValue: group.firstTimestampValue,
      }));
  }, [pathPoints]);

  const toggleFilter = useCallback((tag) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const scrollGalleryToIndex = useCallback((index) => {
    const el = galleryScrollRef.current;
    if (!el) return;
    const target = el.children[index];
    if (!target) return;
    const left = target.offsetLeft - (el.offsetWidth - target.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, []);

  const closeGallery = useCallback(() => {
    setGalleryEntry(null);
    setGalleryInitialSrc(null);
    setGalleryPhotosOverride(null);
    setGalleryIndex(0);
  }, []);

  useEffect(() => {
    if (!galleryEntry || !galleryScrollRef.current) return;
    const el = galleryScrollRef.current;
    let frameId = null;
    const updateClosestIndex = () => {
      const children = Array.from(el.children || []);
      if (!children.length) return;
      const scrollLeft = el.scrollLeft;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (scrollLeft <= 8) {
        if (galleryIndexRef.current !== 0) setGalleryIndex(0);
        return;
      }
      if (maxScroll > 0 && scrollLeft >= maxScroll - 8) {
        const lastIdx = children.length - 1;
        if (galleryIndexRef.current !== lastIdx) setGalleryIndex(lastIdx);
        return;
      }
      const center = scrollLeft + el.clientWidth / 2;
      let closestIdx = 0;
      let minDist = Infinity;
      children.forEach((child, idx) => {
        const rectCenter = child.offsetLeft + child.offsetWidth / 2;
        const dist = Math.abs(rectCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closestIdx = idx;
        }
      });
      if (closestIdx !== galleryIndexRef.current) setGalleryIndex(closestIdx);
    };
    const handleScroll = () => {
      if (frameId != null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        updateClosestIndex();
      });
    };
    const initialIndex = galleryIndexRef.current || 0;
    scrollGalleryToIndex(initialIndex);
    updateClosestIndex();
    el.addEventListener('scroll', handleScroll);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (frameId != null) cancelAnimationFrame(frameId);
    };
  }, [galleryEntry, scrollGalleryToIndex]);

  useEffect(() => {
    galleryIndexRef.current = galleryIndex;
  }, [galleryIndex]);

  const galleryPhotos = useMemo(() => {
    if (Array.isArray(galleryPhotosOverride) && galleryPhotosOverride.length > 0) {
      return galleryPhotosOverride;
    }
    if (!galleryEntry) return [];
    const dateKey =
      typeof galleryEntry?.timestamp === 'string'
        ? galleryEntry.timestamp.slice(0, 10)
        : '';
    return getDatePathPhotoMeta(dateKey);
  }, [galleryEntry, galleryPhotosOverride, getDatePathPhotoMeta]);

  useEffect(() => {
    if (!galleryEntry || !galleryInitialSrc) return;
    if (!galleryPhotos.length) return;
    const idx = galleryPhotos.findIndex((p) => (p && typeof p === 'object' ? p.src : p) === galleryInitialSrc);
    if (idx >= 0 && galleryIndexRef.current !== idx) {
      galleryIndexRef.current = idx;
      setGalleryIndex(idx);
      scrollGalleryToIndex(idx);
    }
  }, [galleryEntry, galleryInitialSrc, galleryPhotos, scrollGalleryToIndex]);

  const galleryPrev = useCallback(() => {
    if (!galleryPhotos.length) return;
    const nextIndex = galleryIndex <= 0 ? galleryPhotos.length - 1 : galleryIndex - 1;
    setGalleryIndex(nextIndex);
    scrollGalleryToIndex(nextIndex);
  }, [galleryPhotos.length, galleryIndex, scrollGalleryToIndex]);

  const galleryNext = useCallback(() => {
    if (!galleryPhotos.length) return;
    const nextIndex = galleryIndex >= galleryPhotos.length - 1 ? 0 : galleryIndex + 1;
    setGalleryIndex(nextIndex);
    scrollGalleryToIndex(nextIndex);
  }, [galleryPhotos.length, galleryIndex, scrollGalleryToIndex]);

  useEffect(() => {
    if (!galleryEntry) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        closeGallery();
        e.preventDefault();
      } else if (e.key === 'ArrowLeft') {
        galleryPrev();
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        galleryNext();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [galleryEntry, closeGallery, galleryPrev, galleryNext]);

  const noop = useCallback(() => {}, []);

  return (
    <MapViewContainer>
      <MapHeader>
        <CmBackButton variant="header" onClick={onBack} ariaLabel="返回內容頁面" />
        <MapTitle>地圖 · 所有地點</MapTitle>
        <MapExternalLink
          href="https://maps.app.goo.gl/Piwk2pLmsAFWkfeZ6"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="在 Google Maps 看清單"
          title="在 Google Maps 看清單"
        >
          <GoogleFavicon>G</GoogleFavicon>
        </MapExternalLink>
      </MapHeader>

      <MapWrap>
        <MapContainer
          center={[25.05, 121.52]}
          zoom={11}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitAllMarkers points={mapPoints} />
          {dateFilter && pathLineSegments.length > 0 && (
            pathLineSegments.map((seg, idx) => (
              <Polyline
                key={`path-line-${idx}`}
                positions={[
                  [seg.from.lat, seg.from.lng],
                  [seg.to.lat, seg.to.lng],
                ]}
                pathOptions={{
                  color: getSegmentColor(idx, pathLineSegments.length),
                  weight: 4,
                  opacity: 0.85,
                }}
              />
            ))
          )}
          {mapPoints.map((point) => {
            const totalForSpot = point.entries.length;
            const isCluster = totalForSpot > 1;
            const clusterLabel = isCluster
              ? `${point.pinNumber}..`
              : String(point.pinNumber);
            return (
              <Marker
                key={point.id}
                position={[point.lat, point.lng]}
                icon={createMapPinIcon(clusterLabel, isCluster)}
                eventHandlers={{
                  click: () => {
                    setSelectedSpotData({ spot: point.spot, entries: point.entries });
                    setSelectedEntry(null);
                  },
                }}
              >
                <Popup>
                  <strong>{point.pinNumber}. {point.name}</strong>
                  <br />
                  <span style={{ opacity: 0.8 }}>{totalForSpot} 條行程</span>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        <FilterBar>
          <FilterTopRow>
            <DateFilterControl>
              <DateNavBtn
                type="button"
                onClick={goPrevDate}
                disabled={!canGoPrevDate}
                aria-label="上一個可選日期"
                title="上一個可選日期"
              >
                ‹
              </DateNavBtn>
              <DatePickerWrap ref={datePickerWrapRef}>
                <DatePickerBtn
                  type="button"
                  onClick={() => setDatePickerOpen((v) => !v)}
                  aria-label="依日期篩選"
                  aria-expanded={datePickerOpen}
                >
                  <span aria-hidden>📅</span>
                  <span>{dateFilter || '全部'}</span>
                </DatePickerBtn>
                {datePickerOpen && (
                  <DatePickerPopover role="dialog" aria-label="日期選擇器">
                    <DayPicker
                      mode="single"
                      selected={selectedDateObject}
                      onSelect={(d) => {
                        if (!d) return;
                        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                        if (!availableDateKeys.has(key)) return;
                        setDateFilter(key);
                        setDatePickerOpen(false);
                      }}
                      fromDate={minAvailableDate}
                      toDate={maxAvailableDate}
                      modifiers={{
                        hasData: (d) => {
                          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                          return availableDateKeys.has(key);
                        },
                      }}
                      disabled={(d) => {
                        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                        return !availableDateKeys.has(key);
                      }}
                      showOutsideDays
                    />
                  </DatePickerPopover>
                )}
              </DatePickerWrap>
              <DateNavBtn
                type="button"
                onClick={goNextDate}
                disabled={!canGoNextDate}
                aria-label="下一個可選日期"
                title="下一個可選日期"
              >
                ›
              </DateNavBtn>
              {dateFilter && (
                <ClearDateButton type="button" onClick={() => setDateFilter('')}>
                  清除日期
                </ClearDateButton>
              )}
            </DateFilterControl>
          </FilterTopRow>

          {availableCategories.length > 0 && (
            <>
              <FilterRow>
                {activeFilters.size > 0 && (
                  <FilterChip
                    type="button"
                    $active={false}
                    onClick={() => setActiveFilters(new Set())}
                    style={{ borderColor: 'rgba(255,120,120,0.5)', color: '#f0a0a0' }}
                  >
                    清除
                  </FilterChip>
                )}
                {availableCategories.map((cat) => (
                  <FilterChip
                    key={cat.id}
                    type="button"
                    $active={expandedCategory === cat.id}
                    onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                  >
                    {cat.label} {expandedCategory === cat.id ? '▾' : '▸'}
                  </FilterChip>
                ))}
              </FilterRow>
              {expandedCategory && (
                <FilterRow>
                  {availableCategories
                    .find((c) => c.id === expandedCategory)
                    ?.tags.map((tag) => (
                      <FilterChip
                        key={tag}
                        type="button"
                        $active={activeFilters.has(tag)}
                        onClick={() => toggleFilter(tag)}
                      >
                        {tag}
                      </FilterChip>
                    ))}
                </FilterRow>
              )}
            </>
          )}
          </FilterBar>

        {selectedSpotData && (
          <SpotSheet>
            <SheetHandle />
            <CloseSheetBtn
              type="button"
              onClick={() => {
                if (selectedEntry) {
                  setSelectedEntry(null);
                  setSelectedPathItem(null);
                } else {
                  setSelectedSpotData(null);
                }
              }}
              aria-label={selectedEntry ? '返回列表' : '關閉面板'}
            >
              {selectedEntry ? '‹' : '×'}
            </CloseSheetBtn>
            {selectedEntry == null ? (
            <>
                <SheetTitle>
                  <span>📍 {selectedSpotData.spot.name}</span>
                  <SheetMiniCardButton
                    type="button"
                    onClick={() => {
                      const first = Array.isArray(selectedSpotData.entries) && selectedSpotData.entries.length > 0
                        ? selectedSpotData.entries[0]
                        : null;
                      if (!first) return;
                      setSelectedEntry(first.entry);
                      setSelectedPathItem(first.pathItem || null);
                    }}
                    aria-label="打開此地點的卡片"
                  >
                    🃏
                  </SheetMiniCardButton>
                </SheetTitle>
                <EntryList>
                  {selectedSpotData.entries.map(({ entry, pathItem, pinNumber }, i) => {
                    const photos = getPathItemPhotos(pathItem);
                    const thumbs = photos.slice(0, 3);
                    return (
                      <EntryChip
                        key={`${entry.id}-${i}`}
                        type="button"
                        onClick={() => { setSelectedEntry(entry); setSelectedPathItem(pathItem); }}
                      >
                        <EntryChipText>
                          <EntryChipDate>
                            #{pinNumber != null ? pinNumber : i + 1}&nbsp;·&nbsp;{entry.timestamp}
                          </EntryChipDate>
                          {(typeof pathItem?.note === 'string' && pathItem.note.trim())
                            ? pathItem.note.trim()
                            : (
                              Array.isArray(pathItem?.tags) && pathItem.tags.length > 0
                                ? pathItem.tags.map((t) => `#${t}`).join(' ')
                                : ''
                            )}
                        </EntryChipText>
                        {thumbs.length > 0 && (
                          <PhotoStack
                            onClick={(e) => {
                              e.stopPropagation();
                              const imgs = Array.isArray(pathItem?.images) ? pathItem.images : [];
                              const photosForGallery = imgs
                                .map((img) => {
                                  const src = img && typeof img === 'object' ? img.url : img;
                                  if (!src) return null;
                                  return { src, entryId: entry.id };
                                })
                                .filter(Boolean);
                              if (!photosForGallery.length) return;
                              setGalleryEntry(entry);
                              setGalleryPhotosOverride(photosForGallery);
                              setGalleryInitialSrc(photosForGallery[0].src);
                              setGalleryIndex(0);
                            }}
                          >
                            {thumbs.map((src, j) => (
                              <StackedThumb
                                key={j}
                                as={ResilientImg}
                                src={src}
                                alt=""
                                loading="lazy"
                                $rotation={
                                  typeof getPhotoRotation === 'function'
                                    ? getPhotoRotation(entry.id, j, src)
                                    : 0
                                }
                              />
                            ))}
                          </PhotoStack>
                        )}
                      </EntryChip>
                    );
                  })}
                </EntryList>
              </>
            ) : (
              <>
                <CardInSheet>
                  <MemoryCard
                    entry={selectedEntry}
                    idx={0}
                    activeIdx={0}
                    totalCount={1}
                    angle={0}
                    getEntryPhotos={getEntryPhotosForSelectedPath}
                    getPhotoRotation={getPhotoRotation}
                    onOpenGallery={(entry, index) => {
                      const clicked = getEntryPhotosForSelectedPath(entry)[index];
                      setGalleryEntry(entry);
                      setGalleryInitialSrc(clicked || null);
                      setGalleryIndex(0);
                    }}
                    onTagClick={onTagClick || noop}
                    isVinylEntry={false}
                    walkSongRef={null}
                    vinylPlaying={false}
                    setVinylPlaying={noop}
                    onWalkSongTimeUpdate={noop}
                    inline
                  />
                </CardInSheet>
              </>
            )}
          </SpotSheet>
        )}
      </MapWrap>

      {galleryEntry && galleryPhotos.length > 0 && (
        <GalleryModal
          photos={galleryPhotos}
          galleryIndex={galleryIndex}
          setGalleryIndex={setGalleryIndex}
          galleryIndexRef={galleryIndexRef}
          scrollGalleryToIndex={scrollGalleryToIndex}
          galleryScrollRef={galleryScrollRef}
          onClose={closeGallery}
          entryId={galleryEntry.id}
          getPhotoRotation={getPhotoRotation}
        />
      )}
    </MapViewContainer>
  );
}
